import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  FormControlLabel,
  Switch,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { Location, LocationFormData } from '../../types/location.types';
import { LocationService } from '../../services/location.service';
import LocationList from '../../components/LocationList/LocationList';
import LocationForm from '../../components/LocationForm/LocationForm';
import { facilityApi } from '@/features/facilities/api/facilityApi';
import ConfirmationDialog from '../../../../../components/common/ConfirmationDialog';
import PageContainer from '../../../../../components/layout/PageContainer';
import { Facility } from '@/features/facilities';

const DEFAULT_PAGE_SIZE = 10;

const LocationsPage: React.FC = () => {
  // Dropdown data state
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [parentLocations, setParentLocations] = useState<Location[]>([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // State for data and loading
  const [locations, setLocations] = useState<Location[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // State for pagination and filtering
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(false);

  // State for modals and forms
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Fetch locations based on current filters and pagination
  const fetchLocations = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const params = {
        page: page + 1, // API pages are 1-based
        limit: rowsPerPage,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        isActive: showActiveOnly ? true : undefined, // Only include isActive in params if showActiveOnly is true
      };
      
      console.log('Fetching locations with params:', params);
      const response = await LocationService.getLocations(params);
      console.log('[LocationsPage] Backend response:', response);
      // Ensure we have an array for locations and a number for total
      const locationsData = Array.isArray(response?.data) ? response.data : [];
      const totalCount = typeof response?.total === 'number' ? response.total : 0;
      
      setLocations(locationsData);
      setTotalRows(totalCount);
      
      console.log(`Fetched ${locationsData.length} of ${totalCount} locations`);
    } catch (error) {
      console.error('Error fetching locations:', error);
      enqueueSnackbar('Failed to load locations', { variant: 'error' });
      setLocations([]); // Ensure locations is always an array
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, sortBy, sortOrder, showActiveOnly, enqueueSnackbar]);
  
  // Initial data load
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);
  
  // Handle search input change with debounce
  useEffect(() => {
    const timerId = setTimeout(() => {
      // Reset to first page when search query changes
      setPage(0);
      fetchLocations();
    }, 500);
    
    return () => clearTimeout(timerId);
  }, [searchQuery, fetchLocations]);
  
  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page
  };
  
  // Handle search input change
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };
  
  // Handle sort
  const handleSort = (field: string) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };
  
  // Open form for creating a new location
  const handleAddNew = async () => {
    setDropdownsLoading(true);
    try {
      const [facilitiesData, locationsData] = await Promise.all([
        facilityApi.getAll(),
        LocationService.getLocations({ limit: 1000 })
      ]);
      setFacilities(facilitiesData);
      setParentLocations(locationsData.data);
      setSelectedLocation(null);
      setIsFormOpen(true);
    } finally {
      setDropdownsLoading(false);
    }
  };
  
  // Open form for editing an existing location
  const handleEdit = async (location: Location) => {
    setDropdownsLoading(true);
    try {
      const [facilitiesData, locationsData] = await Promise.all([
        facilityApi.getAll(),
        LocationService.getLocations({ limit: 1000 })
      ]);
      setFacilities(facilitiesData);
      // Exclude self from parent locations
      setParentLocations(locationsData.data.filter(l => l.id !== location.id));
      setSelectedLocation(location);
      setIsFormOpen(true);
    } finally {
      setDropdownsLoading(false);
    }
  };
  
  // View location details
  const handleView = (location: Location) => {
    navigate(`/settings/locations/${location.id}`);
  };
  
  // Open delete confirmation dialog
  const handleDeleteClick = (location: Location) => {
    setSelectedLocation(location);
    setIsDeleteDialogOpen(true);
  };
  
  // Handle form submission (create/update)
  const handleSubmit = async (values: LocationFormData) => {
    try {
      setIsSubmitting(true);

      if (selectedLocation) {
        // Update existing location
        await LocationService.updateLocation(selectedLocation.id, values);
        enqueueSnackbar('Location updated successfully', { variant: 'success' });
      } else {
        // Create new location
        await LocationService.createLocation(values);
        enqueueSnackbar('Location created successfully', { variant: 'success' });
      }

      // Refresh the list and close the form
      await fetchLocations();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Failed to save location:', error);
      enqueueSnackbar(`Failed to save location: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle location deletion
  const handleDelete = async () => {
    if (!selectedLocation) return;
    
    try {
      setIsSubmitting(true);
      await LocationService.deleteLocation(selectedLocation.id);
      
      enqueueSnackbar('Location deleted successfully', { variant: 'success' });
      handleCloseDeleteDialog();
      
      // If we're on the last page and we just deleted the last item, go back a page
      if (locations.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        // Otherwise, refresh the current page
        await fetchLocations();
      }
    } catch (error) {
      console.error('Failed to delete location:', error);
      enqueueSnackbar(
        `Failed to delete location: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
      setSelectedLocation(null);
    }
  };
  
  // Close form modal
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedLocation(null);
  };
  
  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedLocation(null);
  };
  
  return (
    <PageContainer title="Locations">
      {/* Main content */}
      <Box sx={{ position: 'relative' }}>
        {isLoading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search locations..."
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220, maxWidth: 400 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            disabled={isLoading}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Location
          </Button>
        </Box>
        <LocationList
          locations={locations}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDeleteClick}
          onAddNew={handleAddNew}
          onSearch={handleSearch}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isLoading={isLoading}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      </Box>
      
      {/* Location Form Modal */}
      <Dialog
        open={isFormOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedLocation ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>
        <DialogContent dividers>
          <LocationForm
            initialValues={selectedLocation || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseForm}
            isSubmitting={isSubmitting}
            facilities={facilities}
            parentLocations={parentLocations}
            dropdownsLoading={dropdownsLoading}
          />
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        title="Delete Location"
        message={
          `Are you sure you want to delete ${selectedLocation?.name}? This action cannot be undone.`
        }
        onConfirm={handleDelete}
        onCancel={handleCloseDeleteDialog}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </PageContainer>
  );
};

export default LocationsPage;
